/**
 * Risoluzione sessione app per iframe TechyWeb (incontro + derive).
 * Standard: docs/standards/TL4K_EMBEDDED_APP_BOOT.md
 */
import { COLLAB_API_BASE, authFetch, collabFetch } from "./auth";
import type { EmbeddedDerivedStudentApp } from "./embeddedLaunchUrl";
import { readRateLimitError, readRetryAfterMs } from "./rateLimit";

export type EmbeddedLaunchRole = "teacher" | "student" | null;

/** Tentativi totali sulla lookup derivata quando il server risponde 429. */
const DERIVED_RATE_LIMIT_MAX_ATTEMPTS = 3;

const defaultWait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface RateLimitedDerived {
  rateLimited: true;
  retryAfterMs: number;
  message: string;
}

type DerivedLookup = { pin: string; sessionId: string } | "waiting" | "error" | RateLimitedDerived;

function isRateLimitedDerived(d: DerivedLookup): d is RateLimitedDerived {
  return typeof d === "object" && "rateLimited" in d;
}

export interface EmbeddedLaunchParams {
  incontroPin: string | null;
  appPin: string | null;
  pseudonymId: string | null;
  sessionId: string | null;
  isTeacher: boolean;
  isEmbedded: boolean;
  /** Standalone: `pin` query (sessione app digitata o navigazione locale). */
  standalonePin: string | null;
}

export type AppSessionLaunchResult =
  | { mode: "app"; pin: string; sessionId?: string }
  | { mode: "waiting"; teacherDeriveError?: string }
  | { mode: "standalone"; pin?: string }
  | { mode: "error"; code: "network" | "invalid_pin" | "rate_limited"; message?: string };

export function normalizeSessionPin(raw: string | null | undefined): string | null {
  const t = raw?.trim().toUpperCase();
  if (!t || t.length < 6) return null;
  return t;
}

/**
 * Legge parametri di lancio da query string (contratto unificato + alias legacy).
 */
export function readEmbeddedLaunchParams(search: string): EmbeddedLaunchParams {
  const params = new URLSearchParams(search);
  const isEmbedded = params.get("tl4k-embedded") === "1";
  const role = params.get("tl4k_role");
  const isTeacher = role === "teacher";

  const explicitIncontro = normalizeSessionPin(params.get("incontro_pin"));
  const explicitApp = normalizeSessionPin(params.get("app_pin"));
  const derivedPin = normalizeSessionPin(params.get("derived_pin"));
  const sessionPin = normalizeSessionPin(params.get("session_pin"));
  const tl4kPin = normalizeSessionPin(params.get("tl4k_pin"));
  const standalonePin = normalizeSessionPin(params.get("pin"));
  const pseudonymId = params.get("pseudonym_id")?.trim() || params.get("tl4k_identity")?.trim() || null;
  const sessionId =
    params.get("session_id")?.trim() || params.get("tl4k_session_id")?.trim() || null;

  let appPin = explicitApp ?? derivedPin ?? null;
  if (!appPin && isTeacher && sessionPin) {
    appPin = sessionPin;
  }
  // Vibra studente legacy: session_pin = PIN app derivato
  if (!appPin && !isTeacher && sessionPin && isEmbedded && !derivedPin) {
    appPin = sessionPin;
  }

  let incontroPin = explicitIncontro ?? null;
  if (!incontroPin && tl4kPin && tl4kPin !== appPin) {
    incontroPin = tl4kPin;
  }
  if (!incontroPin && !appPin && tl4kPin) {
    incontroPin = tl4kPin;
  }

  return {
    incontroPin,
    appPin,
    pseudonymId,
    sessionId,
    isTeacher,
    isEmbedded,
    standalonePin,
  };
}

async function lookupDerivedAppPin(
  incontroPin: string,
  appType: EmbeddedDerivedStudentApp,
  apiBase: string
): Promise<DerivedLookup> {
  try {
    const res = await collabFetch(
      `${apiBase}/sessions/by-pin/${encodeURIComponent(incontroPin)}/derived?app=${encodeURIComponent(appType)}`
    );
    if (res.status === 404) return "waiting";
    if (res.status === 429) {
      return {
        rateLimited: true,
        retryAfterMs: readRetryAfterMs(res),
        message: (await readRateLimitError(res)) ?? "Troppi tentativi: riprova tra un minuto",
      };
    }
    if (!res.ok) {
      console.warn(
        "[fetchDerivedAppPin] derived lookup failed:",
        res.status,
        incontroPin,
        appType
      );
      return "error";
    }
    const data = (await res.json()) as {
      derived_pin?: string;
      derived_session_id?: string;
    };
    const pin = normalizeSessionPin(data.derived_pin ?? null);
    if (!pin) return "waiting";
    return {
      pin,
      sessionId: data.derived_session_id?.trim() ?? "",
    };
  } catch (e) {
    console.warn("[fetchDerivedAppPin] network error:", e);
    return "error";
  }
}

/**
 * Docente: la sessione figlia non esiste ancora (il lancio da TechyWeb non l'ha creata o e' fallito in
 * silenzio, SEG-4 2026-10-18: MoMappe restava in "Aspetta che l'insegnante apra" per il docente stesso).
 * Il server e' idempotente (`POST /sessions/:pin/derive` ritrova la figlia attiva) e verifica i permessi:
 * qui si prova a crearla. Ritorna la figlia, oppure il motivo in italiano per cui non e' stato possibile.
 */
export async function deriveAppSessionAsTeacher(
  incontroPin: string,
  appType: EmbeddedDerivedStudentApp,
  apiBase: string = COLLAB_API_BASE,
  fetchImpl: typeof authFetch = authFetch
): Promise<{ pin: string; sessionId: string } | { error: string }> {
  try {
    const res = await fetchImpl(`${apiBase}/sessions/${encodeURIComponent(incontroPin)}/derive`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ app: appType }),
    });
    const body = (await res.json().catch(() => null)) as
      | { pin?: string; session_id?: string; message?: string; error?: string }
      | null;
    if (!res.ok) {
      console.warn("[deriveAppSessionAsTeacher] rifiutata:", res.status, appType, incontroPin, body?.error);
      return { error: body?.message ?? `Il server ha rifiutato la creazione della sessione (HTTP ${res.status}).` };
    }
    const pin = normalizeSessionPin(body?.pin ?? null);
    if (!pin || !body?.session_id) {
      console.warn("[deriveAppSessionAsTeacher] risposta incompleta:", appType, incontroPin);
      return { error: "Risposta incompleta dal server nella creazione della sessione." };
    }
    return { pin, sessionId: body.session_id };
  } catch (e) {
    console.warn("[deriveAppSessionAsTeacher] errore:", e);
    if (/authentication|token|login|non autenticat/i.test(String((e as Error)?.message ?? e))) {
      return { error: "Accesso docente non disponibile in questa finestra." };
    }
    return { error: "Errore di rete nella creazione della sessione." };
  }
}

/**
 * Lookup derivata con backoff: su 429 attende `Retry-After` e riprova (max tentativi),
 * senza mai interpretare il 429 come "sessione non trovata" né ripetere in loop stretto.
 */
async function fetchDerivedWithBackoff(
  incontroPin: string,
  appType: EmbeddedDerivedStudentApp,
  apiBase: string,
  wait: (ms: number) => Promise<void>
): Promise<DerivedLookup> {
  let result = await lookupDerivedAppPin(incontroPin, appType, apiBase);
  for (let attempt = 1; isRateLimitedDerived(result) && attempt < DERIVED_RATE_LIMIT_MAX_ATTEMPTS; attempt++) {
    await wait(result.retryAfterMs);
    result = await lookupDerivedAppPin(incontroPin, appType, apiBase);
  }
  return result;
}

export async function fetchDerivedAppPin(
  incontroPin: string,
  appType: EmbeddedDerivedStudentApp,
  apiBase: string = COLLAB_API_BASE
): Promise<{ pin: string; sessionId: string } | "waiting" | "error"> {
  const result = await fetchDerivedWithBackoff(incontroPin, appType, apiBase, defaultWait);
  if (isRateLimitedDerived(result)) return "error";
  return result;
}

/**
 * Risolve quale sessione app usare al boot (embedded TechyWeb o standalone).
 */
export async function resolveAppSessionLaunch(options: {
  appType: EmbeddedDerivedStudentApp;
  search: string;
  embedded?: boolean;
  role?: EmbeddedLaunchRole;
  apiBase?: string;
  /** Attesa per il backoff su 429 (iniettabile nei test). */
  wait?: (ms: number) => Promise<void>;
  /** Fetch autenticato per la creazione della sessione da parte del docente (iniettabile nei test). */
  teacherFetch?: typeof authFetch;
}): Promise<AppSessionLaunchResult> {
  const params = readEmbeddedLaunchParams(options.search);
  const embedded = options.embedded ?? params.isEmbedded;
  const isTeacher =
    options.role === "teacher" || (options.role == null && params.isTeacher);
  const apiBase = options.apiBase ?? COLLAB_API_BASE;

  if (params.appPin) {
    return {
      mode: "app",
      pin: params.appPin,
      sessionId: params.sessionId ?? undefined,
    };
  }

  if (embedded && params.incontroPin) {
    // Insegnante senza app_pin esplicito: risolvi la sessione derivata (stessa room
    // socket degli studenti). Prima restituiva sempre "waiting" e l'app restava
    // senza PIN derivato finché TechyWeb non aggiornava l'URL.
    const derived = await fetchDerivedWithBackoff(
      params.incontroPin,
      options.appType,
      apiBase,
      options.wait ?? defaultWait
    );
    if (isRateLimitedDerived(derived)) {
      return { mode: "error", code: "rate_limited", message: derived.message };
    }
    if (isTeacher) {
      if (derived === "waiting") {
        const created = await deriveAppSessionAsTeacher(params.incontroPin, options.appType, apiBase, options.teacherFetch);
        if ("error" in created) return { mode: "waiting", teacherDeriveError: created.error };
        return { mode: "app", pin: created.pin, sessionId: created.sessionId };
      }
      if (derived === "error") {
        return { mode: "error", code: "network", message: "Errore di rete." };
      }
      return {
        mode: "app",
        pin: derived.pin,
        sessionId: derived.sessionId || undefined,
      };
    }
    if (derived === "waiting") return { mode: "waiting" };
    if (derived === "error") {
      return { mode: "error", code: "network", message: "Errore di rete." };
    }
    return {
      mode: "app",
      pin: derived.pin,
      sessionId: derived.sessionId || undefined,
    };
  }

  if (!embedded) {
    if (params.standalonePin) {
      return { mode: "standalone", pin: params.standalonePin };
    }
    return { mode: "standalone" };
  }

  return { mode: "standalone" };
}

/**
 * Messaggio UI della schermata **bloccante** storica (`EmbeddedSessionWaiting`).
 *
 * @deprecated La regola (utente, 16/10/2026): l'app resta usabile anche quando la
 * sessione derivata non esiste ancora. In iframe non si blocca l'app con una
 * schermata di attesa: si usa `embeddedSessionWaitingBannerMessage()` con
 * `EmbeddedSessionWaitingBanner` (`@tl4k/admin-ui`) e si continua a lavorare in
 * locale, salvando all'ingresso in sessione.
 */
export const EMBEDDED_SESSION_WAITING_MESSAGE = "Attendi…";

/**
 * Testo della barra **non bloccante** mostrata mentre la sessione derivata non
 * esiste ancora (standard `docs/standards/TL4K_EMBEDDED_APP_BOOT.md`, § UI attesa).
 *
 * `appLabel` è il nome dell'app come lo legge la classe, es. `Disegna`.
 */
/**
 * Banner per il DOCENTE quando la sessione dell'app non si e' potuta creare: il testo per gli studenti
 * ("Aspetta che l'insegnante apra...") qui sarebbe rivolto a lui stesso.
 */
export function embeddedTeacherWaitingBannerMessage(appLabel: string, reason?: string): string {
  const base = `Non sono riuscito a creare la sessione di ${appLabel}: gli studenti non possono ancora entrare.`;
  return reason ? `${base} ${reason}` : `${base} Riapri ${appLabel} dalla sessione.`;
}

export function embeddedSessionWaitingBannerMessage(appLabel: string): string {
  return `Aspetta che l'insegnante apra ${appLabel}: quello che fai adesso viene salvato appena entri nella sessione.`;
}
